/**
 * Client service for Construction Measurement & Bill-Support Packs.
 * Parties, quantity measurements, cumulative totals, revisions, corrections.
 * R39 B6.a + B6.b (plan docs/plans/sector-operations.md row B6 :66).
 * HARD RULE SO-D15: Quantities only. No rates, values, prices or money fields.
 * Wraps callables via callable(); components never import firebase/*.
 */

import { callable } from './callable'
import type {
  ConParty,
  ConMeasurement,
  ConMeasureTotal,
  ConMeasurementRevision,
  ConMeasurementCorrection,
  UpsertConPartyInput,
  ListConPartiesInput,
  SaveConMeasurementInput,
  SubmitConMeasurementInput,
  AcceptConMeasurementInput,
  DisputeConMeasurementInput,
  CorrectConMeasurementInput,
  CancelConMeasurementInput,
  GetConMeasurementInput,
  ListConMeasurementsInput,
  ListDisputedConMeasurementsInput,
  GetConMeasureTotalsInput,
} from '../types/ops'

export async function upsertConParty(
  input: UpsertConPartyInput,
): Promise<{ partyId: string; party: ConParty; replayed?: boolean }> {
  return callable<UpsertConPartyInput, { partyId: string; party: ConParty; replayed?: boolean }>(
    'upsertConParty',
    input,
  )
}

export async function listConParties(
  input: ListConPartiesInput,
): Promise<{ parties: ConParty[]; nextCursor?: string }> {
  return callable<ListConPartiesInput, { parties: ConParty[]; nextCursor?: string }>(
    'listConParties',
    input,
  )
}

export async function saveConMeasurement(
  input: SaveConMeasurementInput,
): Promise<{ measurementId: string; revision: number; measurement: ConMeasurement; replayed?: boolean }> {
  return callable<
    SaveConMeasurementInput,
    { measurementId: string; revision: number; measurement: ConMeasurement; replayed?: boolean }
  >('saveConMeasurement', input)
}

export async function submitConMeasurement(
  input: SubmitConMeasurementInput,
): Promise<{ measurementId: string; state: 'SUBMITTED'; cumulativeAcceptedQty: number; replayed?: boolean }> {
  return callable<
    SubmitConMeasurementInput,
    { measurementId: string; state: 'SUBMITTED'; cumulativeAcceptedQty: number; replayed?: boolean }
  >('submitConMeasurement', input)
}

export async function acceptConMeasurement(
  input: AcceptConMeasurementInput,
): Promise<{ measurementId: string; state: 'ACCEPTED'; acceptedQty: number; cumulativeAcceptedQty: number; replayed?: boolean }> {
  return callable<
    AcceptConMeasurementInput,
    { measurementId: string; state: 'ACCEPTED'; acceptedQty: number; cumulativeAcceptedQty: number; replayed?: boolean }
  >('acceptConMeasurement', input)
}

export async function disputeConMeasurement(
  input: DisputeConMeasurementInput,
): Promise<{ measurementId: string; state: 'DISPUTED'; disputeReason: string; replayed?: boolean }> {
  return callable<
    DisputeConMeasurementInput,
    { measurementId: string; state: 'DISPUTED'; disputeReason: string; replayed?: boolean }
  >('disputeConMeasurement', input)
}

export async function correctConMeasurement(
  input: CorrectConMeasurementInput,
): Promise<{ measurementId: string; acceptedQty: number; cumulativeAcceptedQty: number; correctionNumber: number; replayed?: boolean }> {
  return callable<
    CorrectConMeasurementInput,
    { measurementId: string; acceptedQty: number; cumulativeAcceptedQty: number; correctionNumber: number; replayed?: boolean }
  >('correctConMeasurement', input)
}

export async function cancelConMeasurement(
  input: CancelConMeasurementInput,
): Promise<{ measurementId: string; state: 'CANCELLED'; replayed?: boolean }> {
  return callable<
    CancelConMeasurementInput,
    { measurementId: string; state: 'CANCELLED'; replayed?: boolean }
  >('cancelConMeasurement', input)
}

export async function getConMeasurement(
  input: GetConMeasurementInput,
): Promise<{
  measurement: ConMeasurement
  revisions: ConMeasurementRevision[]
  corrections: ConMeasurementCorrection[]
}> {
  return callable<
    GetConMeasurementInput,
    {
      measurement: ConMeasurement
      revisions: ConMeasurementRevision[]
      corrections: ConMeasurementCorrection[]
    }
  >('getConMeasurement', input)
}

export async function listConMeasurements(
  input: ListConMeasurementsInput,
): Promise<{ measurements: ConMeasurement[]; nextCursor?: string }> {
  return callable<ListConMeasurementsInput, { measurements: ConMeasurement[]; nextCursor?: string }>(
    'listConMeasurements',
    input,
  )
}

export async function listDisputedConMeasurements(
  input: ListDisputedConMeasurementsInput,
): Promise<{ measurements: ConMeasurement[] }> {
  return callable<ListDisputedConMeasurementsInput, { measurements: ConMeasurement[] }>(
    'listDisputedConMeasurements',
    input,
  )
}

export async function getConMeasureTotals(
  input: GetConMeasureTotalsInput,
): Promise<{ totals: ConMeasureTotal[]; nextCursor?: string }> {
  return callable<GetConMeasureTotalsInput, { totals: ConMeasureTotal[]; nextCursor?: string }>(
    'getConMeasureTotals',
    input,
  )
}
